import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Box,
  CircularProgress,
  Pagination,
  MenuItem,
  Slider,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Link,
  Drawer,
  Divider
} from '@mui/material';

import axios from 'axios';

import MusicNoteIcon from '@mui/icons-material/MusicNote';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import PublicIcon from '@mui/icons-material/Public';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';

const PRIMARY = '#A7D8FF';
const API_BASE = process.env.REACT_APP_API_URL || "https://api.zenesarok.net";
const searchableFields = [
  'artist',
  'title',
  'lyrics',
  'country',
  'year',
  'tempo',
  'energy',
  'danceability',
  'popularity'
];

const fallbackCover =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(`
  <svg width="500" height="500" viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg">
    <radialGradient id="g" cx="50%" cy="50%" r="50%">
      <stop offset="0%" stop-color="#222"/>
      <stop offset="100%" stop-color="#000"/>
    </radialGradient>
    <circle cx="250" cy="250" r="240" fill="url(#g)" />
    <circle cx="250" cy="250" r="40" fill="#111" />
    <circle cx="250" cy="250" r="10" fill="#444" />
  </svg>
`);

function App() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [artist, setArtist] = useState(null);
  const [artistOpen, setArtistOpen] = useState(false);

  const debounceRef = useRef(null);

  const [filters, setFilters] = useState({
    keyword: '',
    fields: ['artist', 'title'],
    year_from: 1990,
    year_to: 2025,
    country: '',
    popularity: [0, 100],
    energy: [0, 1],
    danceability: [0, 1],
    tempo: [0, 250]
  });

  const loadSongs = async (targetPage = 1, nextFilters = filters) => {
    setLoading(true);

    try {
      const res = await axios.get(`${API_BASE}/api/songs`, {
        params: {
          ...nextFilters,
          fields: nextFilters.fields.join(','),
          page: targetPage
        }
      });

      setSongs(res.data.data);
      setPage(res.data.current_page);
      setTotalPages(res.data.last_page);
    } finally {
      setLoading(false);
    }
  };

  const openArtist = async (artistName) => {
    try {
      const res = await axios.get(
      `${API_BASE}/api/artists/${encodeURIComponent(artistName)}`
      );
      setArtist(res.data);
      setArtistOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  const triggerSearch = (updated) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadSongs(1, updated);
    }, 350);
  };

  const handleChange = (field, value) => {
    setFilters(prev => {
      const updated = { ...prev, [field]: value };
      triggerSearch(updated);
      return updated;
    });
  };

  useEffect(() => {
    loadSongs(1);
  }, []);

  return (
    <Container maxWidth={false} sx={{ bgcolor: '#121212', minHeight: '100vh', py: 4 }}>

      {/* TITLE */}
      <Typography variant="h3" align="center" sx={{ color: PRIMARY, fontWeight: 'bold', mb: 4 }}>
      <Link href="/" underline="none" color="inherit">
      zenesarok
      </Link>
      </Typography>
      {/* FILTERS */}
      <Card sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Grid container spacing={2}>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Kulcsszó"
              value={filters.keyword}
              onChange={(e) => handleChange('keyword', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Keresési mezők</InputLabel>
              <Select
                multiple
                value={filters.fields}
                onChange={(e) => handleChange('fields', e.target.value)}
                input={<OutlinedInput label="Keresési mezők" />}
              >
                {searchableFields.map(f => (
                  <MenuItem key={f} value={f}>{f}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Ország"
              value={filters.country}
              onChange={(e) => handleChange('country', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => loadSongs(1)}
              sx={{ bgcolor: PRIMARY, color: '#000', height: '100%' }}
            >
              Keresés
            </Button>
          </Grid>

          <Grid item xs={12}>
            <Typography gutterBottom>Year Range</Typography>
            <Slider
              value={[filters.year_from, filters.year_to]}
              min={1950}
              max={2025}
              onChange={(e, v) => {
                handleChange('year_from', v[0]);
                handleChange('year_to', v[1]);
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography gutterBottom>Popularity</Typography>
            <Slider value={filters.popularity}
              onChange={(e, v) => handleChange('popularity', v)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography gutterBottom>Tempo</Typography>
            <Slider value={filters.tempo}
              onChange={(e, v) => handleChange('tempo', v)}
            />
          </Grid>

        </Grid>
      </Card>

      {/* RESULTS */}
      <Grid container spacing={3}>
        {songs.map(song => (
          <Grid item xs={12} md={6} lg={4} key={song.id}>

            <Card sx={{ borderRadius: 3 }}>

              <CardMedia
                component="img"
                height="260"
                image={song.cover_url || fallbackCover}
              />

              <CardContent>

                <Typography variant="h6">{song.title}</Typography>

                {/* 👤 ARTIST CLICKABLE */}
                <Typography
                  sx={{ color: PRIMARY, cursor: 'pointer' }}
                  onClick={() => openArtist(song.artist)}
                >
                  <PersonIcon fontSize="small" /> {song.artist}
                </Typography>

                <Typography variant="body2">{song.year}</Typography>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">Popularity: {song.popularity}</Typography>
                  <Typography variant="body2">Tempo: {song.tempo}</Typography>
                  <Typography variant="body2">Energy: {song.energy}</Typography>
                  <Typography variant="body2">Danceability: {song.danceability}</Typography>
                </Box>

                {/* LINKS */}
                <Box sx={{ mt: 2 }}>
                  <Stack spacing={1}>

                    {song.spotify_url && <Link href={song.spotify_url} target="_blank"><MusicNoteIcon /> Spotify</Link>}
                    {song.youtube_url && <Link href={song.youtube_url} target="_blank"><YouTubeIcon /> YouTube</Link>}
                    {song.musescore_url && <Link href={song.musescore_url} target="_blank"><LibraryMusicIcon /> MuseScore</Link>}
                    {song.chordify_url && <Link href={song.chordify_url} target="_blank"><QueueMusicIcon /> Chordify</Link>}
                    {song.songsterr_url && <Link href={song.songsterr_url} target="_blank"><PublicIcon /> Songsterr</Link>}

                  </Stack>
                </Box>

              </CardContent>
            </Card>

          </Grid>
        ))}
      </Grid>

      {/* PAGINATION */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, v) => loadSongs(v)}
          sx={{
            '& .MuiPaginationItem-root': { color: '#fff' },
            '& .Mui-selected': { background: PRIMARY, color: '#000' }
          }}
        />
      </Box>

      {/* 👤 ARTIST DRAWER */}
      <Drawer anchor="right" open={artistOpen} onClose={() => setArtistOpen(false)}>
        <Box sx={{ width: 400, p: 2, bgcolor: '#111', color: '#fff' }}>

          {artist && (
            <>
              <Typography variant="h5">{artist.name}</Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1">Songs</Typography>
              {artist.songs?.map(s => (
                <Box key={s.id} sx={{ mb: 1 }}>
                  🎵 {s.title}
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1">Concerts</Typography>
              {artist.concerts?.map(c => (
                <Box key={c.id} sx={{ mb: 1 }}>
                  <EventIcon fontSize="small" /> {c.name} - {c.date}

                  {/* koncert dal lista */}
                  {c.songs?.length > 0 && (
                    <Box sx={{ pl: 2 }}>
                      {c.songs.map(cs => (
                        <div key={cs.id}>• {cs.title}</div>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </>
          )}

        </Box>
      </Drawer>

    </Container>
  );
}

export default App;
